// Vendored from orca-front/lib/analytics/portable/ (PR #503). Promote to @kromatic/ab npm package once 3+ sites use this.
/**
 * Portable active-experiments fetcher.
 *
 * Pure `fetch()` wrapper around the orca-back `/experiments/active`
 * endpoint. No axios, no react-query, no framework imports — safe to vendor
 * into Astro `<script>` blocks or any browser runtime with `fetch`.
 *
 * On any failure (network error, non-2xx response, malformed payload) we
 * return `null` and never throw. Callers should fall back to a hardcoded
 * registry or skip the experiment when `null` is returned.
 */

import type { ExperimentConfig } from './types';

const VARIANT_NAME_RE = /^variant_[a-zA-Z0-9_-]+$/;

function isValidVariantName(value: string): boolean {
    return value === 'control' || VARIANT_NAME_RE.test(value);
}

function isValidSurface(value: unknown): value is string {
    return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Runtime type guard for `ExperimentConfig`. Enforces the same invariants as
 * the Next.js fetcher and the in-tree `assertValidExperiment`.
 */
export function isExperimentConfig(value: unknown): value is ExperimentConfig {
    if (!value || typeof value !== 'object') {
        return false;
    }

    const candidate = value as Record<string, unknown>;

    if (typeof candidate.name !== 'string' || !candidate.name.trim()) {
        return false;
    }

    if (
        !Array.isArray(candidate.variants) ||
        candidate.variants.length < 2 ||
        !candidate.variants.every(v => typeof v === 'string' && isValidVariantName(v))
    ) {
        return false;
    }

    if (typeof candidate.control_variant !== 'string' || !candidate.variants.includes(candidate.control_variant)) {
        return false;
    }

    if (
        !Array.isArray(candidate.surfaces) ||
        candidate.surfaces.length < 1 ||
        !candidate.surfaces.every(isValidSurface)
    ) {
        return false;
    }

    if (typeof candidate.active !== 'boolean') {
        return false;
    }

    if (candidate.start_at_ms !== null && typeof candidate.start_at_ms !== 'number') {
        return false;
    }

    if (candidate.end_at_ms !== null && typeof candidate.end_at_ms !== 'number') {
        return false;
    }

    if (
        typeof candidate.start_at_ms === 'number' &&
        typeof candidate.end_at_ms === 'number' &&
        candidate.start_at_ms > candidate.end_at_ms
    ) {
        return false;
    }

    return true;
}

/**
 * Options accepted by `fetchActiveExperiments`. Most callers only need
 * `apiUrl`; advanced sites can swap the `fetch` impl or set request init.
 */
export type FetchActiveExperimentsOptions = {
    /** Optional fetch implementation. Defaults to `globalThis.fetch`. */
    fetchImpl?: typeof fetch;
    /** Optional `RequestInit` overrides (headers, signal, credentials). */
    init?: RequestInit;
};

/**
 * Fetches the active experiment list from `<apiUrl>/experiments/active`.
 *
 * @param apiUrl  Backend base URL (no trailing slash). The `/experiments/active`
 *                path is appended.
 * @param options Optional fetch impl + RequestInit overrides.
 * @returns Validated `ExperimentConfig[]` on success, or `null` on any
 *          failure. Never throws.
 */
export async function fetchActiveExperiments(
    apiUrl: string,
    options: FetchActiveExperimentsOptions = {}
): Promise<ExperimentConfig[] | null> {
    const fetchImpl = options.fetchImpl ?? globalThis.fetch;

    if (typeof fetchImpl !== 'function') {
        return null;
    }

    const trimmedBase = apiUrl.replace(/\/+$/, '');
    const endpoint = `${trimmedBase}/experiments/active`;

    try {
        const response = await fetchImpl(endpoint, options.init);

        if (!response || !response.ok) {
            return null;
        }

        const payload: unknown = await response.json();

        if (!Array.isArray(payload)) {
            return null;
        }

        if (!payload.every(isExperimentConfig)) {
            return null;
        }

        return payload;
    } catch {
        return null;
    }
}
